import type { components as AnalyticsApi } from './generated/analytics';
import type { components as AssessmentApi } from './generated/assessment';
import type { components as CaseApi } from './generated/case';
import type { components as IdentityApi } from './generated/identity';
import { matrixToQuestions } from './adapters';
import { authHeaders, setSession } from './session';
import type { Question } from './types';

type LoginResponse = IdentityApi['schemas']['LoginResponse'];
type MatrixResponse = AssessmentApi['schemas']['AssessmentMatrixResponse'];
type AssessmentProgressResponse = AssessmentApi['schemas']['AssessmentProgressResponse'];
type SaveAnswersRequest = AssessmentApi['schemas']['SaveAnswersRequest'];
type ClientResponse = CaseApi['schemas']['ClientResponse'];
type CaseResponse = CaseApi['schemas']['CaseResponse'];
type CreateCaseRequest = CaseApi['schemas']['CreateCaseRequest'];
type CreateClientRequest = CaseApi['schemas']['CreateClientRequest'];
type ChangePasswordRequest = IdentityApi['schemas']['ChangePasswordRequest'];

/**
 * Production must talk to the real backend by default.
 * Development can still opt in explicitly with VITE_USE_API=true.
 */
export const USE_API = import.meta.env.DEV
  ? import.meta.env.VITE_USE_API === 'true'
  : import.meta.env.VITE_USE_API !== 'false';

type Service = 'identity' | 'cases' | 'question-bank' | 'assessment' | 'analytics' | 'audit';

async function request<T>(service: Service, path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/${service}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...authHeaders(),
      ...(init.headers ?? {})
    }
  });
  if (!res.ok) {
    throw new Error(`${service}${path} -> ${res.status}`);
  }
  return (await res.json()) as T;
}

function persist(res: LoginResponse): LoginResponse {
  if (res.sessionId && res.user) {
    setSession({
      userId: res.user.id,
      userType: res.user.type,
      sessionId: res.sessionId,
      displayName: res.user.displayName,
      mustChangePassword: res.user.mustChangePassword ?? false,
      privilege: 'privilege' in res.user ? res.user.privilege : undefined
    });
  }
  return res;
}

// ---- Auth ----

/** Unified login: the backend resolves Oracle vs Client from the unique identifier. */
export async function login(identifier: string, password: string): Promise<LoginResponse> {
  return persist(
    await request<LoginResponse>('identity', '/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password })
    })
  );
}

export function changePasswordApi(body: ChangePasswordRequest): Promise<{ changed: boolean }> {
  return request<{ changed: boolean }>('identity', '/auth/change-password', {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

// ---- Identity admin ----

type OracleUserResponse = IdentityApi['schemas']['OracleUserResponse'];
type CreateOracleUserRequest = IdentityApi['schemas']['CreateOracleUserRequest'];
type UpdateOracleUserRequest = IdentityApi['schemas']['UpdateOracleUserRequest'];

export async function fetchOracleUsers(): Promise<OracleUserResponse[]> {
  return unwrap<OracleUserResponse>(await request<unknown>('identity', '/users'));
}

export function createOracleUserApi(body: CreateOracleUserRequest): Promise<OracleUserResponse> {
  return request<OracleUserResponse>('identity', '/users', {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

export function updateOracleUserApi(
  userId: string,
  body: UpdateOracleUserRequest
): Promise<OracleUserResponse> {
  return request<OracleUserResponse>('identity', `/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
}

export function resetOracleUserPasswordApi(
  userId: string
): Promise<{ temporaryPassword: string }> {
  return request<{ temporaryPassword: string }>('identity', `/users/${userId}/reset-password`, {
    method: 'POST'
  });
}

/** Permanently deletes an Oracle user (admin-only, enforced server-side). */
export function deleteOracleUserApi(userId: string): Promise<unknown> {
  return request<unknown>('identity', `/users/${userId}`, { method: 'DELETE' });
}

type ClientAccountResponse = IdentityApi['schemas']['ClientAccountResponse'];
type ClientAccountUpsertRequest = IdentityApi['schemas']['ClientAccountUpsertRequest'];

type UpdateClientAccountRequest = IdentityApi['schemas']['UpdateClientAccountRequest'];

export async function fetchClientAccounts(): Promise<ClientAccountResponse[]> {
  return unwrap<ClientAccountResponse>(await request<unknown>('identity', '/client-accounts'));
}

export function upsertClientAccountApi(
  body: ClientAccountUpsertRequest
): Promise<ClientAccountResponse> {
  return request<ClientAccountResponse>('identity', '/client-accounts', {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

/** Permanently deletes a client account (admin-only, enforced server-side). */
export function deleteClientAccountApi(accountId: string): Promise<unknown> {
  return request<unknown>('identity', `/client-accounts/${accountId}`, { method: 'DELETE' });
}

export function updateClientAccountApi(
  accountId: string,
  body: UpdateClientAccountRequest
): Promise<ClientAccountResponse> {
  return request<ClientAccountResponse>('identity', `/client-accounts/${accountId}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
}

// ---- Assessment ----

/** Fetches the matrix and adapts it to the flat Question[] the UI renders. */
export async function fetchMatrix(
  caseId: string,
  dimension: string,
  capability: string,
  personaId: string,
  lang = 'es'
): Promise<Question[]> {
  const query = new URLSearchParams({ dimension, capability, personaId });
  const res = await request<MatrixResponse>(
    'assessment',
    `/cases/${caseId}/matrix?${query.toString()}`
  );
  return matrixToQuestions(res, lang);
}

export function fetchAssessmentProgress(caseId: string): Promise<AssessmentProgressResponse> {
  return request<AssessmentProgressResponse>('assessment', `/cases/${caseId}/progress`);
}

// ---- Analytics (scoring views) ----

type ScoreViewResponse = AnalyticsApi['schemas']['ScoreViewResponse'];
type CreateScoreViewRequest = AnalyticsApi['schemas']['CreateScoreViewRequest'];
type CaseDashboardResponse = AnalyticsApi['schemas']['CaseDashboardResponse'];
type MarkdownExportResponse = AnalyticsApi['schemas']['MarkdownExportResponse'];

export function fetchCaseDashboard(caseId: string): Promise<CaseDashboardResponse> {
  return request<CaseDashboardResponse>('analytics', `/cases/${caseId}/dashboard`);
}

export function exportMarkdownApi(caseId: string): Promise<MarkdownExportResponse> {
  return request<MarkdownExportResponse>('analytics', `/cases/${caseId}/export/markdown`, {
    method: 'POST'
  });
}

export async function fetchScoreViews(caseId: string): Promise<ScoreViewResponse[]> {
  return unwrap<ScoreViewResponse>(await request<unknown>('analytics', `/cases/${caseId}/views`));
}

export function createScoreViewApi(
  caseId: string,
  body: CreateScoreViewRequest
): Promise<ScoreViewResponse> {
  return request<ScoreViewResponse>('analytics', `/cases/${caseId}/views`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

export function deleteScoreViewApi(caseId: string, viewId: string): Promise<unknown> {
  return request<unknown>('analytics', `/cases/${caseId}/score-views/${viewId}`, {
    method: 'DELETE'
  });
}

export function saveAnswers(caseId: string, body: SaveAnswersRequest): Promise<unknown> {
  return request<unknown>('assessment', `/cases/${caseId}/answers`, {
    method: 'PUT',
    body: JSON.stringify(body)
  });
}

// ---- Dev session ----

const DEV_USER = {
  identifier: 'finops.demo@oracle.com',
  password: 'OracleDev1!'
};

export async function ensureDevSession(): Promise<void> {
  await login(DEV_USER.identifier, DEV_USER.password);
}

// ---- Cases & clients ----

function unwrap<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  const items = (data as { items?: unknown })?.items;
  return Array.isArray(items) ? (items as T[]) : [];
}

export async function fetchClients(): Promise<ClientResponse[]> {
  return unwrap<ClientResponse>(await request<unknown>('cases', '/clients'));
}

export async function fetchCases(): Promise<CaseResponse[]> {
  return unwrap<CaseResponse>(await request<unknown>('cases', '/cases'));
}

export function fetchCase(caseId: string): Promise<CaseResponse> {
  return request<CaseResponse>('cases', `/cases/${caseId}`);
}

// ---- Personas (named people per case, each with a role) ----

export type PersonaResponse = CaseApi['schemas']['PersonaResponse'];
export type ConnectedPersonaResponse = CaseApi['schemas']['ConnectedPersonaResponse'];
type CreatePersonaRequest = CaseApi['schemas']['CreatePersonaRequest'];
type UpdatePersonaRequest = CaseApi['schemas']['UpdatePersonaRequest'];

export async function fetchPersonas(caseId: string): Promise<PersonaResponse[]> {
  return unwrap<PersonaResponse>(await request<unknown>('cases', `/cases/${caseId}/personas`));
}

export function createPersonaApi(caseId: string, body: CreatePersonaRequest): Promise<PersonaResponse> {
  return request<PersonaResponse>('cases', `/cases/${caseId}/personas`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

export function updatePersonaApi(
  caseId: string,
  personaId: string,
  body: UpdatePersonaRequest
): Promise<PersonaResponse> {
  return request<PersonaResponse>('cases', `/cases/${caseId}/personas/${personaId}`, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
}

export function deletePersonaApi(caseId: string, personaId: string): Promise<unknown> {
  return request<unknown>('cases', `/cases/${caseId}/personas/${personaId}`, { method: 'DELETE' });
}

// ---- Persona presence (per case, keyed by personaId) ----

export async function fetchConnectedPersonas(caseId: string): Promise<ConnectedPersonaResponse[]> {
  return unwrap<ConnectedPersonaResponse>(
    await request<unknown>('cases', `/cases/${caseId}/personas/connected`)
  );
}

/** Claims a persona (personaId = person+role) for a case. conflict=true on 409 (taken). */
export async function claimPersonaApi(
  caseId: string,
  personaId: string
): Promise<{ ok: boolean; conflict: boolean }> {
  const res = await fetch(`/api/cases/cases/${caseId}/personas/${personaId}/claim`, {
    method: 'POST',
    headers: authHeaders()
  });
  return { ok: res.ok, conflict: res.status === 409 };
}

export function releasePersonaApi(caseId: string, personaId: string): Promise<unknown> {
  return request<unknown>('cases', `/cases/${caseId}/personas/${personaId}/claim`, {
    method: 'DELETE'
  });
}

export function forceLogoutPersonaApi(caseId: string, personaId: string): Promise<unknown> {
  return request<unknown>('cases', `/cases/${caseId}/personas/${personaId}/force-logout`, {
    method: 'POST'
  });
}

export function createClientApi(body: CreateClientRequest): Promise<ClientResponse> {
  return request<ClientResponse>('cases', '/clients', {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

export function createCaseApi(body: CreateCaseRequest): Promise<{ caseId: string }> {
  return request<{ caseId: string }>('cases', '/cases', {
    method: 'POST',
    body: JSON.stringify(body)
  });
}

export function updateDimensionsApi(caseId: string, enabledDimensions: string[]): Promise<unknown> {
  return request<unknown>('cases', `/cases/${caseId}/dimensions`, {
    method: 'PATCH',
    body: JSON.stringify({ enabledDimensions })
  });
}

export function updateStateApi(caseId: string, stateCode: string): Promise<unknown> {
  return request<unknown>('cases', `/cases/${caseId}/state`, {
    method: 'PATCH',
    body: JSON.stringify({ stateCode })
  });
}

/** Archives or unarchives a case (admin-only). */
export function archiveCaseApi(caseId: string, archived: boolean): Promise<unknown> {
  return request<unknown>('cases', `/cases/${caseId}/archive`, {
    method: 'PATCH',
    body: JSON.stringify({ archived })
  });
}

/**
 * Permanently deletes a case (admin-only, irreversible). The admin re-enters
 * their password; the backend re-verifies it before deleting. Returns
 * `{ ok:false, unauthorized:true }` on a 401/403 (wrong password / not admin).
 */
export async function deleteCaseApi(
  caseId: string,
  password: string
): Promise<{ ok: boolean; unauthorized: boolean }> {
  const res = await fetch(`/api/cases/cases/${caseId}`, {
    method: 'DELETE',
    headers: { 'content-type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ password })
  });
  return { ok: res.ok, unauthorized: res.status === 401 || res.status === 403 };
}

/** The active (published) question bank version id, needed to create a case. */
export async function fetchActiveQuestionBankVersionId(): Promise<string | null> {
  const items = unwrap<{ id: string; status?: string }>(
    await request<unknown>('question-bank', '/versions')
  );
  const published = items.find((v) => v.status === 'PUBLISHED') ?? items[0];
  return published?.id ?? null;
}

export interface QuestionBankRowApiResponse {
  id: string;
  questionId: string;
  dimension: string;
  capability: string;
  persona: string;
  lens: string;
  questionText?: Record<string, string>;
  answerOptions?: Array<{ value: number; text?: Record<string, string> }>;
}

export async function fetchQuestionBankRowsApi(
  versionId: string
): Promise<QuestionBankRowApiResponse[]> {
  return unwrap<QuestionBankRowApiResponse>(
    await request<unknown>('question-bank', `/versions/${versionId}/rows`)
  );
}

// ---- Audit / activity log ----

/**
 * Immutable activity event as returned by audit-service `GET /events`.
 * The contract (audit-service.yaml) does not yet define the response schema,
 * so this shape is typed locally from the live payload; when the contract adds
 * it, replace this with the generated type.
 */
export interface AuditEvent {
  id: string;
  eventType: string;
  eventTime: string;
  actorType: string;
  actorId: string | null;
  sessionId: string | null;
  entityType: string;
  entityId: string;
}

/** Most-recent-first activity events from audit-service. */
export async function fetchActivityEvents(): Promise<AuditEvent[]> {
  const items = unwrap<AuditEvent>(await request<unknown>('audit', '/events'));
  return [...items].sort((a, b) => b.eventTime.localeCompare(a.eventTime));
}
